/**
 * Confirm an action on the rollout (D40, vendor.admin): what it does, and a reason — kept as a halt's or roll back's
 * reason and in the activity log. A halt and a roll back need one; the others may go without.
 */
import type { RolloutActionWire } from '@norna/api-contract/rollouts.js';
import { type FormEvent, useState } from 'react';
import { shortHash } from '../../lib/vendor.js';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Field, FieldError } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';

export type RolloutActionTarget = { readonly action: RolloutActionWire; readonly listHash: string };

export type RolloutActionDialogProps = {
  readonly target: RolloutActionTarget | null;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (request: { readonly action: RolloutActionWire; readonly listHash: string; readonly reason: string }) => void;
};

const TEXT: Readonly<Record<RolloutActionWire, { readonly title: string; readonly what: string; readonly submit: string }>> = {
  pause: { title: 'Pause the rollout', what: 'No further ring opens until you resume it. A rejection still halts it.', submit: 'Pause' },
  resume: { title: 'Resume the rollout', what: 'The gate opens rings again. A rejection still in force halts it at the next pass: clear it first.', submit: 'Resume' },
  advance: { title: 'Open the next ring now', what: 'Skips the open ring’s gate: its soak, its productions and its alarms. With every ring open, completes the rollout.', submit: 'Advance' },
  halt: { title: 'Halt the rollout', what: 'Servers already on the new release keep it; no other server gets it.', submit: 'Halt' },
  rollback: { title: 'Roll back', what: 'Every ring is served the previous release again. Servers move between shows, through their janitor, as with a pin.', submit: 'Roll back' },
};

/** A halt and a roll back say why. */
function needsReason(action: RolloutActionWire): boolean {
  return action === 'halt' || action === 'rollback';
}

function ActionForm({ target, pending, error, onClose, onSubmit }: RolloutActionDialogProps & { readonly target: RolloutActionTarget }) {
  const [reason, setReason] = useState('');
  const text = TEXT[target.action];
  const ready = !needsReason(target.action) || reason.trim() !== '';
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready || pending) return;
    onSubmit({ action: target.action, listHash: target.listHash, reason: reason.trim() });
  };
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>{text.title}</DialogTitle>
        <DialogDescription>
          Release <span className="font-mono">{shortHash(target.listHash)}</span>. {text.what}
        </DialogDescription>
      </DialogHeader>
      <Field>
        <Label htmlFor="rollout-reason">Reason{needsReason(target.action) ? '' : ' (optional)'}</Label>
        <Input id="rollout-reason" value={reason} onChange={(e) => setReason(e.target.value)} disabled={pending} maxLength={1000} placeholder="Audio drop on the canary sites" />
      </Field>
      <FieldError>{error}</FieldError>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" variant={needsReason(target.action) ? 'destructive' : 'default'} disabled={!ready || pending} data-testid="rollout-action-submit">
          {pending ? 'Working…' : text.submit}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function RolloutActionDialog(props: RolloutActionDialogProps) {
  const { target } = props;
  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
    >
      <DialogContent data-testid="rollout-action-dialog">{target === null ? null : <ActionForm {...props} target={target} key={`${target.action}:${target.listHash}`} />}</DialogContent>
    </Dialog>
  );
}
